export type PlanFeatureKey =
  | "clientRevenue"
  | "subcontractors"
  | "suppliers"
  | "payroll"
  | "expenses"
  | "budget"
  | "boq"
  | "retention"
  | "promissory"
  | "cashFlow"
  | "reports"
  | "commercialDocs"
  | "purchaseOrders"
  | "voDescope"
  | "lineItems"
  | "multiCompany"
  | "taxConfig"
  | "docSequencing"
  | "emailInvites"
  | "apiAccess"
  | "customBranding"
  | "projectTeams"
  | "attachments"
  | "printExport"
  | "cfo"
  | "accounting";

export type PlanFeatures = Record<PlanFeatureKey, boolean>;

export interface PlanDef {
  id: string;
  label: string;
  short: string;
  price: string;
  priceMonthly: number;
  priceYearly: number;
  yearlyDiscountPct: number;
  maxProjects: number;
  maxUsers: number;
  color: string;
  badgeCls: string;
  description: string;
  track: "lite" | "full" | "enterprise" | "accounting";
  features: PlanFeatures;
  highlights: string[];
  popular?: boolean;
}

export const ALL_FEATURE_KEYS: PlanFeatureKey[] = [
  "clientRevenue",
  "subcontractors",
  "suppliers",
  "payroll",
  "expenses",
  "budget",
  "boq",
  "retention",
  "promissory",
  "cashFlow",
  "reports",
  "commercialDocs",
  "purchaseOrders",
  "voDescope",
  "lineItems",
  "multiCompany",
  "taxConfig",
  "docSequencing",
  "emailInvites",
  "apiAccess",
  "customBranding",
  "projectTeams",
  "attachments",
  "printExport",
  "cfo",
  "accounting",
];

export const FEATURE_LABELS: Record<PlanFeatureKey, string> = {
  clientRevenue:  "Client Revenue (Invoices & Receipts)",
  subcontractors: "Subcontractor Management",
  suppliers:      "Supplier Management",
  payroll:        "Payroll & Staff",
  expenses:       "Expense Tracking",
  budget:         "Budget Management",
  boq:            "Bill of Quantities (BOQ)",
  retention:      "Retention Management",
  promissory:     "Promissory Notes",
  cashFlow:       "Cash Flow Management",
  reports:        "Financial Reports & Analytics",
  commercialDocs: "Quotations & Commercial Proposals",
  purchaseOrders: "Purchase Orders",
  voDescope:      "Variation Orders & Descopes",
  lineItems:      "Itemized Line Items Table",
  multiCompany:   "Multi-Company Workspace",
  taxConfig:      "Advanced Tax Configuration",
  docSequencing:  "Custom Document Numbering",
  emailInvites:   "Team Email Invitations",
  apiAccess:      "API Access",
  customBranding: "Custom Colors & Branding",
  projectTeams:   "Project Team Management",
  attachments:    "File Attachments (up to 6 MB each)",
  printExport:    "Print & PDF Export of Documents",
  cfo:            "CFO View Dashboard",
  accounting:     "Double-Entry Accounting (GL, Trial Balance, Financials)",
};

export const FEATURE_GROUPS: { label: string; keys: PlanFeatureKey[] }[] = [
  {
    label: "Finance Modules",
    keys: ["clientRevenue", "subcontractors", "suppliers", "payroll", "expenses", "budget"],
  },
  {
    label: "Advanced Finance",
    keys: ["boq", "retention", "promissory", "cashFlow", "reports", "cfo"],
  },
  {
    label: "Commercial",
    keys: ["commercialDocs", "purchaseOrders", "voDescope", "lineItems"],
  },
  {
    label: "Platform",
    keys: ["attachments", "multiCompany", "taxConfig", "docSequencing", "emailInvites", "projectTeams", "customBranding", "apiAccess"],
  },
  {
    label: "Accounting Module",
    keys: ["accounting"],
  },
];

const ALL_ON: PlanFeatures = {
  clientRevenue:  true,
  subcontractors: true,
  suppliers:      true,
  payroll:        true,
  expenses:       true,
  budget:         true,
  boq:            true,
  retention:      true,
  promissory:     true,
  cashFlow:       true,
  reports:        true,
  commercialDocs: true,
  purchaseOrders: true,
  voDescope:      true,
  lineItems:      true,
  multiCompany:   true,
  taxConfig:      true,
  docSequencing:  true,
  emailInvites:   true,
  apiAccess:      true,
  customBranding: true,
  projectTeams:   true,
  attachments:    true,
  printExport:    true,
  cfo:            true,
  accounting:     true,
};

const ALL_OFF: PlanFeatures = {
  clientRevenue:  false,
  subcontractors: false,
  suppliers:      false,
  payroll:        false,
  expenses:       false,
  budget:         false,
  boq:            false,
  retention:      false,
  promissory:     false,
  cashFlow:       false,
  reports:        false,
  commercialDocs: false,
  purchaseOrders: false,
  voDescope:      false,
  lineItems:      false,
  multiCompany:   false,
  taxConfig:      false,
  docSequencing:  false,
  emailInvites:   false,
  apiAccess:      false,
  customBranding: false,
  projectTeams:   false,
  attachments:    false,
  printExport:    false,
  cfo:            false,
  accounting:     false,
};

export const PLANS: PlanDef[] = [
  /* ── Lite ── */
  {
    id:                "lite",
    label:             "Lite",
    short:             "Lite",
    price:             "$10/mo",
    priceMonthly:      10,
    priceYearly:       100,
    yearlyDiscountPct: 17,
    maxProjects:       15,
    maxUsers:          10,
    color:             "bg-slate-500",
    badgeCls:          "bg-slate-500/10 text-slate-600",
    track:             "full",
    description:       "Variation orders, descopes, subcontractor certs, cash flow, custom doc numbering.",
    features: {
      ...ALL_OFF,
      clientRevenue:  true,
      subcontractors: true,
      suppliers:      true,
      payroll:        true,
      expenses:       true,
      budget:         true,
      boq:            true,
      cashFlow:       true,
      commercialDocs: true,
      purchaseOrders: true,
      voDescope:      true,
      lineItems:      true,
      docSequencing:  true,
      emailInvites:   true,
      attachments:    true,
      projectTeams:   true,
      printExport:    true,
    },
    highlights: [
      "15 projects · 10 users",
      "Full client invoicing + VOs & descopes",
      "SC VOs & descopes",
      "Subcontractor & supplier management",
      "Cash flow tracking",
      "Custom document numbering",
      "Payroll & expenses",
    ],
  },
  /* ── Lite Pro ── */
  {
    id:                "lite-pro",
    label:             "Lite Pro",
    short:             "Lite Pro",
    price:             "$25/mo",
    priceMonthly:      25,
    priceYearly:       250,
    yearlyDiscountPct: 17,
    maxProjects:       35,
    maxUsers:          15,
    color:             "bg-blue-400",
    badgeCls:          "bg-blue-400/10 text-blue-500",
    track:             "full",
    description:       "Everything in Lite plus reports, CFO view, retention, and file attachments.",
    features: {
      ...ALL_OFF,
      clientRevenue:  true,
      subcontractors: true,
      suppliers:      true,
      payroll:        true,
      expenses:       true,
      budget:         true,
      boq:            true,
      retention:      true,
      promissory:     true,
      cashFlow:       true,
      reports:        true,
      commercialDocs: true,
      purchaseOrders: true,
      voDescope:      true,
      lineItems:      true,
      docSequencing:  true,
      emailInvites:   true,
      attachments:    true,
      projectTeams:   true,
      printExport:    true,
      cfo:            true,
    },
    highlights: [
      "35 projects · 15 users",
      "Everything in Lite",
      "Financial Reports & Analytics",
      "CFO View Dashboard",
      "Retention Management",
      "Promissory Notes & PDC",
    ],
  },
  /* ── Pro ── */
  {
    id:                "pro",
    label:             "Pro",
    short:             "Pro",
    price:             "$60/mo",
    priceMonthly:      60,
    priceYearly:       600,
    yearlyDiscountPct: 17,
    maxProjects:       75,
    maxUsers:          25,
    color:             "bg-indigo-500",
    badgeCls:          "bg-indigo-500/10 text-indigo-600",
    track:             "full",
    description:       "Full finance suite with advanced tax, multi-company, custom branding, and API access.",
    popular:           true,
    features: {
      ...ALL_OFF,
      clientRevenue:  true,
      subcontractors: true,
      suppliers:      true,
      payroll:        true,
      expenses:       true,
      budget:         true,
      boq:            true,
      retention:      true,
      promissory:     true,
      cashFlow:       true,
      reports:        true,
      commercialDocs: true,
      purchaseOrders: true,
      voDescope:      true,
      lineItems:      true,
      multiCompany:   true,
      taxConfig:      true,
      docSequencing:  true,
      emailInvites:   true,
      apiAccess:      true,
      customBranding: true,
      projectTeams:   true,
      attachments:    true,
      printExport:    true,
      cfo:            true,
    },
    highlights: [
      "75 projects · 25 users",
      "Everything in Lite Pro",
      "Multi-company workspace",
      "API access & integrations",
      "Custom branding",
      "VAT & tax configuration",
    ],
  },
  /* ── Pro Plus ── */
  {
    id:                "pro-plus",
    label:             "Pro Plus",
    short:             "Pro+",
    price:             "$100/mo",
    priceMonthly:      100,
    priceYearly:       1000,
    yearlyDiscountPct: 17,
    maxProjects:       120,
    maxUsers:          -1,
    color:             "bg-emerald-600",
    badgeCls:          "bg-emerald-600/10 text-emerald-700",
    track:             "accounting",
    description:       "All features including full double-entry accounting — chart of accounts, journal entries, GL, trial balance, balance sheet, and income statement.",
    popular:           false,
    features: { ...ALL_ON },
    highlights: [
      "120 projects · Unlimited users",
      "Everything in Pro",
      "Double-Entry Accounting (GL, Trial Balance)",
      "Balance Sheet & Income Statement",
      "Chart of Accounts (seeded COA)",
      "Auto-posting from transactions",
    ],
  },
];

const LEGACY_PLAN_MAP: Record<string, string> = {
  "solo": "lite", "contractor": "lite", "Starter": "lite",
  "lite-s": "lite", "lite-m": "lite",
  "lite-l": "lite-pro", "Professional": "lite-pro",
  "group": "pro-plus", "Enterprise": "pro-plus",
};

export function getPlan(planId: string | undefined | null): PlanDef {
  if (!planId) return PLANS.find(p => p.id === "pro") ?? PLANS[0];
  const normalized = LEGACY_PLAN_MAP[planId] ?? planId;
  return PLANS.find(p => p.id === normalized) ?? PLANS[0];
}

export function hasFeature(
  planIdOrFeatures: string | Partial<PlanFeatures> | undefined | null,
  feature: PlanFeatureKey,
): boolean {
  if (!planIdOrFeatures) return getPlan(null).features[feature] ?? false;
  if (typeof planIdOrFeatures === "string") return getPlan(planIdOrFeatures).features[feature] ?? false;
  return planIdOrFeatures[feature] ?? false;
}

export function getLimit(planId: string | undefined | null, key: "maxProjects" | "maxUsers"): number {
  return getPlan(planId)[key];
}

export function isWithinLimit(planId: string | undefined | null, key: "maxProjects" | "maxUsers", current: number): boolean {
  const limit = getLimit(planId, key);
  return limit === -1 || current < limit;
}

export type EffectiveLimits = { maxProjects: number; maxUsers: number };

export function mergeEffectiveFeatures(
  planId: string | undefined | null,
  planConfigFeatures: Partial<PlanFeatures> | null | undefined,
  companyOverrides: Partial<PlanFeatures> | null | undefined,
): PlanFeatures {
  const base = getPlan(planId).features;
  return {
    ...base,
    ...(planConfigFeatures ?? {}),
    ...(companyOverrides ?? {}),
  };
}

export function mergeEffectiveLimits(
  planId: string | undefined | null,
  planConfigLimits: { maxProjects?: number | null; maxUsers?: number | null } | null | undefined,
): EffectiveLimits {
  const base = getPlan(planId);
  return {
    maxProjects: planConfigLimits?.maxProjects ?? base.maxProjects,
    maxUsers:    planConfigLimits?.maxUsers    ?? base.maxUsers,
  };
}
